import React from 'react';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Inbox } from 'lucide-react';
import { useFormatter } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';

// ── Formatted financial amount (reads decimal places from system settings)
export const FormattedAmount = ({ value, currency, className = '' }) => {
  const { fmt, systemCurrency } = useFormatter();
  const cur = currency || systemCurrency;
  return (
    <span className={`font-mono [font-variant-numeric:tabular-nums] ${className}`}>
      {fmt(value)}{cur ? ` ${cur}` : ''}
    </span>
  );
};

// ── Formatted number (no currency symbol)
export const FormattedNumber = ({ value, className = '' }) => {
  const { fmt } = useFormatter();
  return (
    <span className={`font-mono [font-variant-numeric:tabular-nums] ${className}`}>
      {fmt(value)}
    </span>
  );
};

// ── Status Badge (dot + label, tonal)
export const StatusBadge = ({ status }) => {
  const map = {
    PENDING:     'status-pending',
    APPROVED:    'status-approved',
    REJECTED:    'status-rejected',
    DORMANT:     'status-dormant',
    ORDER:       'status-order',
    RFQ:         'status-rfq',
    DRAFT:       'status-draft',
    TEMPLATE:    'status-template',
    UNAPPROVED:  'status-pending',
    ACTIVE:      'status-approved',
    INACTIVE:    'status-dormant',
    YES:         'status-approved',
    NO:          'status-dormant',
  };
  return (
    <span className={`status-badge ${map[status?.toUpperCase()] || 'status-dormant'}`}>
      {status}
    </span>
  );
};

// ── Active Badge
export const ActiveBadge = ({ active }) => (
  <span className={`status-badge ${active ? 'status-approved' : 'status-dormant'}`}>
    {active ? 'Yes' : 'No'}
  </span>
);

// ── Favourite Star
export const FavoriteStar = ({ active, onToggle, 'data-testid': testId }) => {
  const { t } = useTranslation();
  return (
    <button
      onClick={e => { e.stopPropagation(); onToggle?.(); }}
      aria-label={active ? t('term.removeFavourite') : t('term.addFavourite')}
      aria-pressed={active}
      data-testid={testId || 'table-row-favorite-toggle'}
      className="p-1 rounded-md transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
    >
      <svg className="h-4 w-4 transition-colors" viewBox="0 0 24 24"
        stroke={active ? '#f59e0b' : '#a0aec0'} strokeWidth={1.5}
        fill={active ? '#f59e0b' : 'none'}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
      </svg>
    </button>
  );
};

// ── Page Header
import HelpButton from '../../pages/Guides/HelpButton';

export const PageHeader = ({ title, description, actions, helpGuide }) => (
  <div className="page-header">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {title && (
        <div className="flex items-center gap-2">
          <h1 className="page-title">{title}</h1>
          {helpGuide && <HelpButton guideId={helpGuide} label={`${title} Guide`} />}
        </div>
      )}
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      {actions && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="page-header-actions">{actions}</div>
      )}
    </div>
  </div>
);

// ── Pagination Bar
export const PaginationBar = ({ page, pageSize, total, onPageChange, onPageSizeChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-3 border-t mt-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Show</span>
        <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}
          className="h-7 rounded-md border border-input bg-background px-2 text-xs"
          aria-label="Records per page" data-testid="pagination-page-size-select">
          {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-muted-foreground">
          {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
        </span>
      </div>
      <div className="flex items-center gap-1" data-testid="pagination-control">
        {[['\xab', 1], ['\u2039', page - 1], ['\u203a', page + 1], ['\xbb', totalPages]].map(([label, target]) => (
          <button key={label} onClick={() => onPageChange(target)}
            disabled={target < 1 || target > totalPages || target === page}
            className="h-7 w-7 rounded-md border border-border flex items-center justify-center text-xs disabled:opacity-35 hover:bg-accent transition-colors"
            aria-label={String(label)}>{label}</button>
        ))}
      </div>
    </div>
  );
};

// ── Collapsible Section
export const CollapsibleSection = ({ title, children, defaultOpen = true, className }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className={cn('section-panel', className)}>
      <button onClick={() => setOpen(!open)} className="section-panel-header w-full" aria-expanded={open}>
        <span>{title}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? '' : '-rotate-180'}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-5 py-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Loading Spinner
export const LoadingSpinner = ({ size = 'md' }) => {
  const sizes = { sm: 'h-4 w-4 border', md: 'h-8 w-8 border-2', lg: 'h-12 w-12 border-2' };
  return (
    <div className="flex items-center justify-center" aria-live="polite" aria-label="Loading">
      <div className={`${sizes[size]} animate-spin rounded-full border-muted border-t-primary`} />
    </div>
  );
};

// ── Empty State
export const EmptyState = ({ title = 'No records found', description, action }) => (
  <div className="empty-state">
    <div className="empty-state-icon">
      <Inbox className="h-5 w-5 text-muted-foreground" />
    </div>
    <div className="space-y-1">
      <h3 className="text-sm font-semibold">{title}</h3>
      {description && <p className="text-xs text-muted-foreground max-w-xs">{description}</p>}
    </div>
    {action && <div>{action}</div>}
  </div>
);

// ── Search / Table Toolbar
export const DataTableToolbar = ({ searchValue, onSearch, onClear, searchPlaceholder = 'Search...', actions, toolbarSlotId }) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-4">
    <div className="relative flex-1 max-w-xs">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="text" value={searchValue} onChange={e => onSearch(e.target.value)}
        placeholder={searchPlaceholder}
        className="search-input w-full pl-9 pr-8"
        aria-label="Search"
        data-testid="data-table-search-input"
      />
      {searchValue && (
        <button onClick={onClear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label="Clear search">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
    {toolbarSlotId && <div id={toolbarSlotId} className="flex items-center" data-testid={`${toolbarSlotId}-slot`} />}
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

// ── Placeholder / Coming Soon page
export const PlaceholderPage = ({ title, icon: Icon }) => (
  <div className="placeholder-page">
    {Icon && (
      <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-5">
        <Icon className="h-7 w-7 text-muted-foreground/50" />
      </div>
    )}
    <h2 className="text-lg font-semibold text-foreground mb-1">{title}</h2>
    <p className="text-sm text-muted-foreground max-w-sm">
      This module is <strong>scheduled to be built</strong>. It will be available in a future release.
    </p>
    <div className="mt-5 px-4 py-2 rounded-full border border-dashed border-primary/30 bg-primary/5">
      <p className="text-xs font-medium text-primary">Coming Soon</p>
    </div>
  </div>
);
